import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import "./Home.css";
import TreeVineMobile from "../../components/TreeVine/TreeVineMobile";
import useTreeScroll from "../../components/TreeCanvas/useTreeScroll";
import profilePhoto from "./Images/me.png";

/* ---------- Branch section definitions (mobile) ---------- */
// Scroll ranges: [enterStart, enterEnd, exitStart, exitEnd]
// Non-overlapping so only one card is visible at a time
const SECTIONS = [
  {
    label: "Energy",
    to: "/energy",
    desc: "SCADA, HVPP & market systems",
    icon: "⚡",
    range: [0.20, 0.28, 0.38, 0.44],
    side: "left",
    top: "30%",
  },
  {
    label: "Robotics",
    to: "/robotics",
    desc: "Autonomous systems & hardware",
    icon: "🤖",
    range: [0.46, 0.54, 0.62, 0.68],
    side: "right",
    top: "50%",
  },
  {
    label: "Arctic",
    to: "/arctic",
    desc: "AI chatbots & LLM solutions",
    icon: "❄️",
    range: [0.70, 0.78, 0.86, 0.92],
    side: "left",
    top: "70%",
  },
];

/* ---------- Card animation state machine ---------- */
function getCardPhase(progress, range) {
  const [enterStart, enterEnd, exitStart, exitEnd] = range;
  if (progress < enterStart) return "hidden";
  if (progress < enterEnd) return "entering";
  if (progress < exitStart) return "sitting";
  if (progress < exitEnd) return "exiting";
  return "gone";
}

const mobileCardVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.92 },
  entering: {
    opacity: 1, y: 0, scale: 1,
    transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
  },
  sitting: {
    opacity: 1, y: 0, scale: 1,
    transition: { duration: 0.15 },
  },
  exiting: {
    opacity: 0, y: -30, scale: 0.95,
    transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1] },
  },
  gone: { opacity: 0, y: -30, scale: 0.95 },
};

/* ---------- Mobile branch card ---------- */
function MobileBranchCard({ section, progress }) {
  const phase = getCardPhase(progress, section.range);

  if (phase === "hidden" || phase === "gone") return null;

  return (
    <div className="home-mobile-vine-card-center">
      <motion.div
        className="home-mobile-vine-card"
        variants={mobileCardVariants}
        initial="hidden"
        animate={phase}
      >
        <Link to={section.to} className="home-branch-card-inner home-mobile-vine-card-inner">
          <div className="home-branch-card-icon">{section.icon}</div>
          <div className="home-branch-card-body">
            <span className="home-branch-card-label">{section.label}</span>
            <span className="home-branch-card-desc">{section.desc}</span>
          </div>
          <span className="home-branch-card-arrow">→</span>
        </Link>
      </motion.div>
    </div>
  );
}

/* ---------- HomeMobile ---------- */
function HomeMobile() {
  const containerRef = useRef(null);
  const progress = useTreeScroll(containerRef);
  const [heroVisible, setHeroVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setHeroVisible(true), 200);
    return () => clearTimeout(timer);
  }, []);

  const heroOpacity = Math.max(0, 1 - progress * 5);

  return (
    <div className="home-mobile-vine-container" ref={containerRef}>
      {/* SVG vine backdrop — mobile-specific shape */}
      <div className="home-mobile-vine-backdrop">
        <TreeVineMobile progress={progress} />
      </div>

      {/* Sticky viewport with hero + cards */}
      <div className="home-mobile-vine-viewport">
        {/* Hero — fades out on scroll */}
        <motion.div
          className={`home-tree-hero home-mobile-vine-hero ${heroVisible ? "visible" : ""}`}
          style={{ opacity: heroOpacity, pointerEvents: heroOpacity < 0.3 ? "none" : "auto" }}
        >
          <h1 className="home-hero-name-text">Eugen Dunca</h1>
          <p className="home-tree-hero-tagline">
            Engineering intelligence for energy systems
          </p>
          <div className="home-scroll-indicator">
            <span>Scroll to explore</span>
            <div className="home-scroll-arrow" />
          </div>
        </motion.div>

        {/* Branch cards — animate in/out with scroll */}
        {SECTIONS.map((section) => (
          <MobileBranchCard key={section.label} section={section} progress={progress} />
        ))}
      </div>

      {/* Scroll spacer */}
      <div className="home-mobile-vine-spacer" />

      {/* About Me card */}
      <div className="home-about-section home-mobile-vine-about-section">
          <div className="home-about-card home-about-card--has-photo">
            <img
              src={profilePhoto}
              alt="Eugen Dunca"
              className="home-about-card-photo"
              loading="lazy"
              width="300"
              height="300"
            />
            <h2 className="home-about-card-title">About Me</h2>
            <p className="home-about-card-text">
              I'm Eugen Dunca — an energy systems engineer building SCADA infrastructure,
              AI-driven dispatch optimization, and hybrid virtual power plant platforms at Waldevar Energy.
              My work spans real-time monitoring with Ignition, time series data pipelines, and
              ML-based load forecasting.
            </p>
            <p className="home-about-card-subtext">
              This portfolio is a window into my work across energy, robotics, and AI — built for those
              who value where technology meets real-world impact.
            </p>
            <Link to="/experience" className="home-about-card-btn">
              View My Experience →
            </Link>
          </div>
      </div>

      {/* Bottom section */}
      <div className="home-bottom-section home-mobile-vine-bottom">
        <motion.blockquote
          className="home-quote-new"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <p>"The impediment to action advances action. What stands in the way becomes the way."</p>
          <cite>— Marcus Aurelius</cite>
        </motion.blockquote>

        <div className="home-bottom-cta">
          <p className="home-bottom-cta-text">
            Interested in my energy systems work? Explore the details or reach out.
          </p>
          <div className="home-bottom-cta-links">
            <Link to="/experience" className="home-bottom-cta-btn home-bottom-cta-btn--primary">
              View More →
            </Link>
            <Link to="/about" className="home-bottom-cta-btn home-bottom-cta-btn--secondary">
              About Me
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HomeMobile;
